interface CacheEntry<T> {
    resourceType: string;
    page: number;
    parameters?: Map<string, string>;
    pageSize: number | undefined;
    data: T[];
    timestamp: Date;
}

interface QueryCache<T> {
    [key: string]: CacheEntry<T>;
}

const cache: QueryCache<any> = {};

export function checkForCacheEntry(
    resourceType: string,
    page: number = -1,
    parameters?: Map<string, string>,
    pageSize?: number,
): boolean {
    const cacheEntry = cache[resourceType];

    if (!cacheEntry) {
        return false;
    } else {
        if (new Date().getTime() - cacheEntry.timestamp.getTime() > 9000000) {
            delete cache[resourceType];
            return false;
        }

        return checkForParameterMatch(resourceType, page, parameters, pageSize);
    }
}

export function fetchFromCache<T>(resourceType: string): T[] {
    return cache[resourceType].data;
}

export function saveToCache<T>(
    data: T[],
    resourceType: string,
    page: number = -1,
    parameters?: Map<string, string>,
    pageSize?: number,
): void {
    cache[resourceType] = {
        resourceType,
        page,
        parameters,
        pageSize,
        data,
        timestamp: new Date(),
    };
}

export function updateCachedResourceRow<T>(resource: T, resourceId: string, resourceType: string): void {
    const cacheEntry = cache[resourceType];

    if (!cacheEntry) {
        return;
    }

    const cachedResourceIndex = cacheEntry.data.findIndex((cachedResource) => cachedResource.id === resourceId);
    cacheEntry.timestamp = new Date();

    if (cachedResourceIndex > -1) {
        const updatedResource = {
            ...cacheEntry.data[cachedResourceIndex],
            ...resource,
        };

        cacheEntry.data.splice(cachedResourceIndex, 1, updatedResource);
    } else {
        delete cache[resourceType];
    }
}

function checkForParameterMatch(
    resourceType: string,
    page: number = -1,
    parameters?: Map<string, string>,
    pageSize?: number,
): boolean {
    const cachedEntry = cache[resourceType];

    if (cachedEntry.page !== page) {
        return false;
    }

    if (cachedEntry.pageSize !== pageSize) {
        return false;
    }

    if (cachedEntry.parameters && !parameters) {
        return false;
    }

    let allMatch = true;

    if (parameters && cachedEntry.parameters) {
        if (parameters.size !== cachedEntry.parameters.size) {
            return false;
        }

        for (const key of parameters.keys()) {
            const parameter = parameters.get(key);
            const cachedParameter = cachedEntry.parameters.get(key);

            if (cachedParameter !== parameter) {
                allMatch = false;
                break;
            }
        }

        return allMatch;
    } else {
        return false;
    }
}

export function deleteFromCache(resourceType: string): void {
    const cacheEntry = cache[resourceType];

    if (!cacheEntry) {
        return;
    }

    delete cache[resourceType];
}
